import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

const authHeader = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

// ─── Repediu ─────────────────────────────────────────────────────────────────

export interface IntegracaoRepediu {
    codigoRepediu: string | null
    chaveRepediu: string | null
}

export const obterRepediu = async function (
    authorization: string
): Promise<IntegracaoRepediu | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoRepediu>>("integracaoParceiros/ObterRepediu", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoRepediu>(response, "ObterRepediu")
    } catch (error) {
        RegistrarLogErro(error, "ObterRepediu", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarRepediu = async function (
    authorization: string,
    payload: IntegracaoRepediu
): Promise<IntegracaoRepediu | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoRepediu>>("integracaoParceiros/SalvarRepediu", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoRepediu>(response, "SalvarRepediu")
    } catch (error) {
        RegistrarLogErro(error, "SalvarRepediu", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── DigiSac ─────────────────────────────────────────────────────────────────

export interface IntegracaoDigiSac {
    serviceId: string | null
    urlPlataforma: string | null
    tokenDigiSac: string | null
    ativo: boolean
}

export const obterDigiSac = async function (
    authorization: string
): Promise<IntegracaoDigiSac | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoDigiSac>>("integracaoParceiros/ObterDigiSac", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoDigiSac>(response, "ObterDigiSac")
    } catch (error) {
        RegistrarLogErro(error, "ObterDigiSac", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarDigiSac = async function (
    authorization: string,
    payload: IntegracaoDigiSac
): Promise<IntegracaoDigiSac | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoDigiSac>>("integracaoParceiros/SalvarDigiSac", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoDigiSac>(response, "SalvarDigiSac")
    } catch (error) {
        RegistrarLogErro(error, "SalvarDigiSac", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export interface TesteDigiSac {
    mensagem: string
}

export const testarDigiSac = async function (
    authorization: string,
    telefone: string
): Promise<TesteDigiSac | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<TesteDigiSac>>(
                "integracaoParceiros/TestarDigiSac",
                { telefone },
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<TesteDigiSac>(response, "TestarDigiSac")
    } catch (error) {
        RegistrarLogErro(error, "TestarDigiSac", telefone)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── PagSeguro (somente leitura: token atual + URL de autorização) ───────────

export interface IntegracaoPagSeguro {
    tokenLoja: string | null
    urlAutorizacao: string
}

export const obterPagSeguro = async function (
    authorization: string
): Promise<IntegracaoPagSeguro | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoPagSeguro>>("integracaoParceiros/ObterPagSeguro", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoPagSeguro>(response, "ObterPagSeguro")
    } catch (error) {
        RegistrarLogErro(error, "ObterPagSeguro", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── Uber ────────────────────────────────────────────────────────────────────

export interface IntegracaoUber {
    clienteId: string | null
    secretId: string | null
    costumerId: string | null
    observacaoRetirada: string | null
    minutosChamarEntregador: number | null
    retornarItemNaoEntregue: boolean
    automatico: boolean
    ativo: boolean
}

export const obterUber = async function (authorization: string): Promise<IntegracaoUber | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoUber>>("integracaoParceiros/ObterUber", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoUber>(response, "ObterUber")
    } catch (error) {
        RegistrarLogErro(error, "ObterUber", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarUber = async function (
    authorization: string,
    payload: IntegracaoUber
): Promise<IntegracaoUber | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoUber>>("integracaoParceiros/SalvarUber", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<IntegracaoUber>(response, "SalvarUber")
    } catch (error) {
        RegistrarLogErro(error, "SalvarUber", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
